import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaEnvelope, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { API } from '../config/api';

const TestContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TestCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #2d3436;
  text-align: center;
  margin-bottom: 1rem;
  font-size: 2.5rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #636e72;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #2d3436;
  font-weight: 600;
  font-size: 1rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:invalid {
    border-color: #e74c3c;
  }
`;

const TestButton = styled(motion.button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusCard = styled(motion.div)`
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: 10px;
  border-left: 4px solid ${props => props.success ? '#4CAF50' : '#e74c3c'};
  background: ${props => props.success ? '#e8f5e8' : '#ffeaea'};
`;

const StatusTitle = styled.h3`
  color: ${props => props.success ? '#2e7d32' : '#c62828'};
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
`;

const StatusMessage = styled.p`
  color: ${props => props.success ? '#2e7d32' : '#c62828'};
  margin: 0;
  line-height: 1.5;
`;

const StatusDetails = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  font-size: 0.9rem;
  color: #555;
`;

const LoadingSpinner = styled(motion.div)`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  margin-right: 0.5rem;
`;

const ConfigInfo = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const ConfigTitle = styled.h3`
  color: #2d3436;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
`;

const ConfigItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const ConfigLabel = styled.span`
  color: #636e72;
  font-weight: 600;
`;

const ConfigValue = styled.span`
  color: #2d3436;
  font-family: monospace;
  background: #e9ecef;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
`;

function EmailTest() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API}/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: data.message,
          details: data.details
        });
      } else {
        setResult({
          success: false,
          message: data.error,
          details: data.details
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: 'Network error - make sure the server is running on port 5000',
        details: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TestContainer>
      <TestCard
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Title>📧 Gmail Connection Test</Title>
        <Subtitle>
          Test your Gmail configuration to ensure emails can be sent successfully
        </Subtitle>

        <ConfigInfo>
          <ConfigTitle>Current Configuration</ConfigTitle>
          <ConfigItem>
            <ConfigLabel>Email User:</ConfigLabel>
            <ConfigValue>winternorth04@gmail.com</ConfigValue>
          </ConfigItem>
          <ConfigItem>
            <ConfigLabel>App Password:</ConfigLabel>
            <ConfigValue>{'*'.repeat(16)}</ConfigValue>
          </ConfigItem>
          <ConfigItem>
            <ConfigLabel>Service:</ConfigLabel>
            <ConfigValue>Gmail SMTP</ConfigValue>
          </ConfigItem>
          <ConfigItem>
            <ConfigLabel>Server Port:</ConfigLabel>
            <ConfigValue>5000</ConfigValue>
          </ConfigItem>
        </ConfigInfo>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="testEmail">Test Email Address</Label>
            <Input
              id="testEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email to receive a test message"
              required
            />
          </InputGroup>

          <TestButton
            type="submit"
            disabled={loading || !email}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
          >
            {loading && (
              <LoadingSpinner
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            )}
            {loading ? 'Sending Test Email...' : 'Send Test Email'}
          </TestButton>
        </Form>

        {result && (
          <StatusCard
            success={result.success}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <StatusTitle success={result.success}>
              {result.success ? '✅ Test Successful!' : '❌ Test Failed'}
            </StatusTitle>
            <StatusMessage success={result.success}>
              {result.message}
            </StatusMessage>
            {result.details && (
              <StatusDetails>
                <strong>Details:</strong><br />
                {typeof result.details === 'object' ? (
                  <>
                    From: {result.details.from}<br />
                    To: {result.details.to}<br />
                    Time: {new Date(result.details.timestamp).toLocaleString()}
                  </>
                ) : (
                  result.details
                )}
              </StatusDetails>
            )}
          </StatusCard>
        )}

        {result?.success && (
          <StatusCard
            success={true}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <StatusTitle success={true}>📬 Next Steps</StatusTitle>
            <StatusMessage success={true}>
              Check your email inbox for the test message. If you received it, your Gmail configuration is working perfectly and ready for the newsletter system!
            </StatusMessage>
          </StatusCard>
        )}
      </TestCard>
    </TestContainer>
  );
}

export default EmailTest;
